import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';

import { useUserContext } from '../../../contexts/user-context/UserContext';
import { useTheme } from '@mui/material';
import { useOrganizations } from '../../../queries/organizations/UseOrganizations';
import { useQueryNotifications } from '../../../queries/UseQueryNotifications';
import { IOrganization } from '../../../services/organization-data-service/Models';
import OrganizationHierarchyUtils from '../OrganizationHierarchyUtils';
import { OrganizationTreeItem } from './OrganizationTreeItem';
import { TreeViewItemId } from '@mui/x-tree-view';

export type OrganizationHierarchyProps = {
    includeAllOption?: boolean;
    organizationID: number,
    onSelected: (selectedItem: IOrganization) => void;
    onLoading?: (isLoading: boolean) => void;
}

export default function OrganizationHierarchy({
    includeAllOption,
    organizationID,
    onSelected, 
    onLoading
}: OrganizationHierarchyProps) {
    
    const [userState] = useUserContext();

    const theme = useTheme();

    const treeViewRef = useRef<HTMLUListElement>(null);

    const [selected, setSelected] = useState<string>(organizationID.toString());
    const [expanded, setExpanded] = useState<string[]>([]);

    const {data: organizations} = useQueryNotifications(useOrganizations)({
        includeAllOption
    });
    const treeData = OrganizationHierarchyUtils.getTreeData(organizations, includeAllOption ? -1 : userState.user?.baseGroup?.organizationID ?? -1);

    useEffect(() => {

        if (onLoading) {
            onLoading(treeData === undefined);
        }
    }, [treeData,
        onLoading]);    

    useEffect(() => {

        const getItemsToExpand = async () => {

            let itemsToExpand: string[] = [];

            const ancestors = OrganizationHierarchyUtils.getAncestors(organizations, organizationID);

            if (ancestors) {
                itemsToExpand = ancestors?.map(o => o.id.toString());
            }

            itemsToExpand.push(organizationID.toString());
            setExpanded(itemsToExpand.reverse());            
        };

        if (expanded.length === 0) {
            getItemsToExpand();
        }

    }, [organizations,
        organizationID,
        expanded,
        setExpanded
    ]);


    function onExpandedItemsChange(_event: React.SyntheticEvent | null, itemIds: TreeViewItemId[]) {
        setExpanded(itemIds);
    };

    const onLabelClick = (event: React.MouseEvent<Element, MouseEvent>, item: IOrganization) => {        

        onSelected(item);
        setSelected(item.id.toString());

        event.preventDefault();
    };
    
    const renderTree = (item: IOrganization) => (
        <OrganizationTreeItem key={item.id} 
                itemId={item.id.toString()}
                showIcon={true}
                navigationItem={item}
                onLabelClick={(e: React.MouseEvent<Element, MouseEvent>) => onLabelClick(e, item)}
            >
          {
            Array.isArray(item.children) ? 
                item.children.map((node) => renderTree(node)) :
                null
          }
        </OrganizationTreeItem>
      );
    
      return (
          <SimpleTreeView
              ref={treeViewRef}
              sx={{
                  flexGrow: 1,
                  width: 400,
                  [theme.breakpoints.only('xs')]: {
                    width: '100%'
                  }
              }}
              slots={{
                  collapseIcon: ExpandMoreIcon,
                  expandIcon: ChevronRightIcon
              }}
              expandedItems={expanded}
              selectedItems={selected}
              onExpandedItemsChange={onExpandedItemsChange}
          >
              {
                treeData && renderTree(treeData)
              }
          </SimpleTreeView>
      );
  }
